import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { MessageSchema } from './primitives.js';
const c = initContract();
/**
 * G-01 — THE FAVOURITES BAR (bug-triage plan, Oct 2026; INV-H8 is the same
 * star seen from a document page).
 *
 * A person pins the screens they live on. The star in the top bar adds or
 * removes the CURRENT route; the slim bar under it lists what they pinned, in
 * their own order. Everything here is the person's own: no route takes a user
 * id in a path, a query or a body, so there is nothing to forge — the same
 * shape ADM-04 chose for the bell, for the same reason.
 *
 * The LABEL is the person's own text, captured from the page heading at the
 * moment of adding (a document page captures its number). It is stored, not
 * derived: a favourite is "the thing I called this", and re-deriving it from
 * the route would lose the document number the moment the screen is renamed.
 *
 * A route is a path inside the SPA (`/sales/…`), never a full URL — the bar
 * draws it as an in-app link and a stored `https://` would be a navigation
 * out of the product.
 */
export const FAVORITE_LIMIT = 50;
export const FavoriteRouteSchema = z
    .string()
    .min(1)
    .max(300)
    // A leading slash, no scheme, no whitespace: a path the router can take.
    .regex(/^\/[^\s]*$/);
export const FavoriteSchema = z
    .object({
    id: z.uuid(),
    route: FavoriteRouteSchema,
    label: z.string(),
    sortOrder: z.number().int(),
})
    .strict();
/** Every write answers with the whole list, so the bar never merges. */
export const FavoriteListResponseSchema = z
    .object({ favorites: z.array(FavoriteSchema).max(FAVORITE_LIMIT) })
    .strict();
export const AddFavoriteRequestSchema = z
    .object({
    route: FavoriteRouteSchema,
    label: z.string().trim().min(1).max(120),
})
    .strict();
/**
 * The person's ids in the order they want them. Ids not named keep their
 * place after the named ones; an id that is not theirs is ignored, not an
 * error — the list the response carries is the truth either way.
 */
export const ReorderFavoritesRequestSchema = z
    .object({ ids: z.array(z.uuid()).min(1).max(FAVORITE_LIMIT) })
    .strict();
export const favoritesContract = c.router({
    list: {
        method: 'GET',
        path: '/api/favorites',
        responses: { 200: FavoriteListResponseSchema, 401: MessageSchema },
        summary: 'G-01: the caller’s own favourites, in their order. Never anybody else’s; no permission.',
    },
    /**
     * Idempotent: adding a route already pinned refreshes nothing and answers
     * with the list as it stands. At FAVORITE_LIMIT the insert is skipped
     * rather than refused — the web disables the star there, and the list in
     * the answer says what happened without a sentence.
     */
    add: {
        method: 'POST',
        path: '/api/favorites',
        body: AddFavoriteRequestSchema,
        responses: { 200: FavoriteListResponseSchema, 400: MessageSchema, 401: MessageSchema },
        summary: 'G-01: pin the current route under the heading the person saw. Idempotent per route.',
    },
    remove: {
        method: 'DELETE',
        path: '/api/favorites/:id',
        pathParams: z.object({ id: z.uuid() }).strict(),
        body: z.object({}).strict(),
        responses: { 200: FavoriteListResponseSchema, 401: MessageSchema },
        summary: 'G-01: unpin. A colleague’s id and one that never existed both answer with the unchanged list.',
    },
    reorder: {
        method: 'PUT',
        path: '/api/favorites/order',
        body: ReorderFavoritesRequestSchema,
        responses: { 200: FavoriteListResponseSchema, 400: MessageSchema, 401: MessageSchema },
        summary: 'G-01: the person’s own order for the bar.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=favorites.js.map